import { useRef, useEffect } from 'react';

function useReveal(delay = 0) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => el.classList.add('visible'), delay);
          obs.unobserve(el);
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay]);
  return ref;
}

const GOOGLE_REVIEWS_URL = 'https://share.google/dnbTBsPiMKPytal2X';

const googleReviews = [
  {
    name: 'Sarah M.',
    rating: 5,
    date: '2 months ago',
    text: "Kendrick's inspection background was a total game-changer for us. He caught things during showings that saved us thousands in repairs. His knowledge of home systems is unmatched — I felt confident in every single decision we made. Highly recommend!",
    initials: 'SM',
    color: '#4285F4',
  },
  {
    name: 'Tom R.',
    rating: 5,
    date: '3 months ago',
    text: 'He made selling my parents\' estate so much easier than I expected. Honest, patient, and he truly cared about getting it right. Kendrick treated our family\'s home with the respect it deserved. Couldn\'t have asked for a better agent.',
    initials: 'TR',
    color: '#34A853',
  },
  {
    name: 'Jennifer & Mike L.',
    rating: 5,
    date: '4 months ago',
    text: 'We were buying and selling at the same time — a logistical nightmare — and Kendrick coordinated everything perfectly. No stress, just results. He kept us informed every step of the way and made the whole process feel seamless.',
    initials: 'JL',
    color: '#EA4335',
  },
  {
    name: 'Michael S.',
    rating: 5,
    date: '5 months ago',
    text: "Kendrick's inspection insights during negotiations saved us over $8,000 in repairs. He knew exactly what to look for and how to use that knowledge to our advantage. Worth his weight in gold — we can't thank him enough!",
    initials: 'MS',
    color: '#FBBC05',
  },
  {
    name: 'Amanda K.',
    rating: 5,
    date: '6 months ago',
    text: 'As a first-time buyer, I had a million questions. Kendrick answered every single one and never once made me feel rushed or overwhelmed. His patience and expertise made what could have been a stressful process genuinely enjoyable.',
    initials: 'AK',
    color: '#4285F4',
  },
  {
    name: 'David P.',
    rating: 5,
    date: '7 months ago',
    text: 'Professional, knowledgeable, and genuinely cares about his clients. Kendrick made the whole selling process smooth and stress-free. He priced our home perfectly and we had multiple offers within the first week. Absolutely stellar service.',
    initials: 'DP',
    color: '#34A853',
  },
];

function StarRating({ count = 5 }: { count?: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <i
          key={i}
          className={`ri-star-fill text-sm ${i < count ? 'text-[#FBBC05]' : 'text-gray-200'}`}
        ></i>
      ))}
    </div>
  );
}

function GoogleIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

export default function GoogleReviews() {
  const summaryRef = useReveal(100);
  const card0 = useReveal(0); const card1 = useReveal(100); const card2 = useReveal(200);
  const card3 = useReveal(0); const card4 = useReveal(100); const card5 = useReveal(200);
  const cardRefs = [card0, card1, card2, card3, card4, card5];
  const ctaRef = useReveal(0);

  return (
    <section className="py-14 sm:py-20 bg-white border-t border-gray-100">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">

        {/* Rating Summary Badge */}
        <div ref={summaryRef} className="reveal flex justify-center mb-10 sm:mb-14">
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 sm:gap-6 bg-[#FAF9F7] border border-gray-100 rounded-2xl px-6 sm:px-10 py-6 sm:py-7 w-full sm:w-auto max-w-lg sm:max-w-none">
            {/* Google Logo + Score */}
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center">
                <GoogleIcon size={40} />
              </div>
              <div>
                <div className="flex items-baseline gap-2">
                  <span className="font-cormorant text-5xl font-semibold text-[#111111] leading-none">5.0</span>
                  <span className="text-sm text-[#888] font-medium">on Google</span>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <StarRating count={5} />
                  <span className="text-xs text-[#888]">6 reviews</span>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="hidden sm:block w-px h-12 bg-gray-200"></div>
            <div className="sm:hidden w-full h-px bg-gray-200"></div>

            {/* Trust text */}
            <div className="text-center sm:text-left max-w-xs">
              <p className="text-sm text-[#333] leading-relaxed">
                <strong className="text-[#111111]">100% 5-star rated.</strong> Buyers and sellers across Southeast Michigan trust Kendrick for honest guidance and exceptional results.
              </p>
            </div>
          </div>
        </div>

        {/* Review Cards Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 mb-10 sm:mb-14">
          {googleReviews.map((review, i) => (
            <div
              key={i}
              ref={cardRefs[i]}
              className="reveal scale bg-[#FAF9F7] rounded-xl p-7 hover-lift border border-gray-100 flex flex-col"
            >
              {/* Card Header */}
              <div className="flex items-start justify-between mb-5">
                <div className="flex items-center gap-3">
                  {/* Avatar */}
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs font-semibold"
                    style={{ backgroundColor: review.color }}
                  >
                    {review.initials}
                  </div>
                  <div>
                    <p className="font-semibold text-[#111111] text-sm leading-tight">{review.name}</p>
                    <p className="text-xs text-[#999] mt-0.5">{review.date}</p>
                  </div>
                </div>
                {/* Google Icon */}
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <GoogleIcon size={18} />
                </div>
              </div>

              {/* Stars */}
              <StarRating count={review.rating} />

              {/* Review Text */}
              <p className="text-sm text-[#444] leading-relaxed mt-4 flex-1">
                &ldquo;{review.text}&rdquo;
              </p>

              {/* Footer */}
              <div className="mt-5 pt-4 border-t border-gray-100 flex items-center gap-1.5">
                <GoogleIcon size={14} />
                <span className="text-xs text-[#999]">Posted on Google</span>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Buttons */}
        <div ref={ctaRef} className="reveal flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
          <a
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2.5 px-8 py-4 bg-[#1C3A6B] text-white text-sm font-semibold rounded-md hover:bg-[#152E55] transition-all whitespace-nowrap hover-lift"
          >
            <GoogleIcon size={16} />
            Read All Reviews on Google
          </a>
          <a
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2.5 px-8 py-4 border border-[#1C3A6B]/30 text-[#1C3A6B] text-sm font-semibold rounded-md hover:bg-[#1C3A6B]/5 transition-all whitespace-nowrap hover-lift"
          >
            <i className="ri-edit-line text-base"></i>
            Leave a Review
          </a>
        </div>

      </div>
    </section>
  );
}
